import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import {
  getActiveCart,
  getSeedBookingErrorMessage,
  removeCartItem,
  submitSeedBookingOrder,
} from "./SeedBookingApi";
import {
  EmptyState,
  LoadingState,
  PageHeader,
  formatAmount,
  formatQuantity,
  getSeedBookingDistributorName,
} from "./SeedBookingUi";
import "./SeedBooking.css";

const SeedBookingCartSummary = () => {
  const distributorId = localStorage.getItem("distributorId") || "";
  const isBpUser = localStorage.getItem("userType") === "BPUser";
  const distributorName = isBpUser ? getSeedBookingDistributorName(distributorId) : "";
  const pageBase = isBpUser ? "/BPUDashboard/SeedBooking" : "/SeedBooking";
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [comments, setComments] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!distributorId) {
      setLoading(false);
      return undefined;
    }

    let active = true;
    getActiveCart(distributorId)
      .then((result) => active && setCart(result))
      .catch((error) => active && toast.error(getSeedBookingErrorMessage(error)))
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [distributorId]);

  const handleRemove = async (cartItemId) => {
    setBusy(true);
    try {
      setCart(await removeCartItem({
        distributorId: Number(distributorId),
        cartItemId,
      }));
      toast.success("Item removed from cart.");
    } catch (error) {
      toast.error(getSeedBookingErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!cart?.items?.length) {
      toast.error("Add at least one product before submitting.");
      return;
    }

    setBusy(true);
    try {
      const result = await submitSeedBookingOrder({
        distributorId: Number(distributorId),
        comments,
      });
      toast.success(isBpUser ? "Seed booking submitted and approved." : "Seed booking submitted successfully.");
      navigate(`${pageBase}/OrderSummary/${result.seedBookingOrderLineId}`, { replace: true });
    } catch (error) {
      toast.error(getSeedBookingErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const items = cart?.items || [];
  const totalQuantity = items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);

  return (
    <section className="content seed-booking-page">
      <div className="container-fluid">
        <PageHeader
          icon="fa-shopping-cart"
          title="Review booking"
          subtitle={isBpUser
            ? "Check the items below, then submit the booking."
            : "Check the items below, then submit the booking for review."}
          distributorName={distributorName}
        >
          <Link to={pageBase} className="btn sb-btn sb-btn-outline">
            <i className="fa fa-arrow-left" aria-hidden="true"></i> Back to products
          </Link>
        </PageHeader>

        {!distributorId ? (
          <EmptyState icon="fa-user-circle" title="No distributor selected">
            Select a distributor before reviewing a seed booking cart.
            <Link to={pageBase} className="btn sb-btn sb-btn-primary">Open Seed Booking</Link>
          </EmptyState>
        ) : loading ? (
          <LoadingState label="Loading cart..." />
        ) : !items.length ? (
          <EmptyState icon="fa-shopping-cart" title="Your cart is empty">
            Add products with Qtl and amount to create a booking.
            <Link to={pageBase} className="btn sb-btn sb-btn-primary">Browse products</Link>
          </EmptyState>
        ) : (
          <div className="sb-split">
            <div className="sb-card sb-split-main">
              <div className="sb-card-title">
                <h2>Cart items</h2>
                <span className="sb-pill">{items.length} {items.length === 1 ? "item" : "items"}</span>
              </div>
              <ul className="sb-rows">
                {items.map((item) => (
                  <li key={item.id} className="sb-row">
                    <div className="sb-row-main">
                      <span className="sb-row-title">{item.productItemDisplayName || item.productItemName}</span>
                      <small>{item.productLineDisplayName || item.productLineName}</small>
                    </div>
                    <div className="sb-row-figure">
                      <small>Qtl</small>
                      <span>{formatQuantity(item.quantity)}</span>
                    </div>
                    <div className="sb-row-figure sb-row-amount">
                      <small>Amount</small>
                      <span>{formatAmount(item.totalCost)}</span>
                    </div>
                    <button
                      type="button"
                      className="btn sb-btn sb-btn-icon"
                      onClick={() => handleRemove(item.id)}
                      disabled={busy}
                      title="Remove"
                      aria-label={`Remove ${item.productItemDisplayName || item.productItemName}`}
                    >
                      <i className="fa fa-trash-o" aria-hidden="true"></i>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <form className="sb-card sb-split-side" onSubmit={handleSubmit}>
              <div className="sb-card-title">
                <h2>Booking summary</h2>
              </div>
              <dl className="sb-totals">
                <div>
                  <dt>Products</dt>
                  <dd>{items.length}</dd>
                </div>
                <div>
                  <dt>Total Qtl</dt>
                  <dd>{formatQuantity(totalQuantity)}</dd>
                </div>
                <div className="sb-totals-grand">
                  <dt>Booking amount</dt>
                  <dd>{formatAmount(cart.subTotal)}</dd>
                </div>
              </dl>
              <div className="sb-form-group">
                <label htmlFor="seedBookingComments">Comments <span>(optional)</span></label>
                <textarea
                  id="seedBookingComments"
                  className="form-control"
                  maxLength="4000"
                  rows="3"
                  placeholder="Delivery preferences, notes for the sales team..."
                  value={comments}
                  onChange={(event) => setComments(event.target.value)}
                />
              </div>
              <button type="submit" className="btn sb-btn sb-btn-primary sb-btn-block" disabled={busy}>
                {busy ? (
                  <><span className="spinner-border spinner-border-sm" aria-hidden="true"></span> Submitting...</>
                ) : (
                  <><i className="fa fa-paper-plane" aria-hidden="true"></i> Submit booking</>
                )}
              </button>
              <p className="sb-hint">
                {isBpUser
                  ? "Bookings placed by the sales team are approved automatically."
                  : "The booking will be sent to the sales team for approval."}
              </p>
            </form>
          </div>
        )}
      </div>
      <ToastContainer position="bottom-center" autoClose={3500} limit={2} />
    </section>
  );
};

export default SeedBookingCartSummary;
